import ControlViewModel from '@/ViewModels/ControlViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import TypeConverter from '@/Utility/TypeConverter';
import Utilities from '@/Utility/Utilities';
export default class NumericTextBoxViewModel extends ControlViewModel {
    /**
     * Der Wert der TextBox
     */
    public value: KnockoutObservable<string>;
    public displayValue: KnockoutObservable<string>;

    /**
     * Gibt an, ob unbegrenzte Dezimalstellen verwendet werden sollen
     */
    public useUnlimitedDecimals: boolean;

    /**
     * Gibt die Präzision an
     */
    public floatingPrecision: KnockoutObservable<any> | any;
    /**
     * Gibt das Format an, standardmäßig null (es wird dem Control überlassen, wie die Zahlen dargestellt werden sollen, ggf. unter Zuhilfenahme von floatingPrecision).
     * Schreibweise: siehe http://docs.telerik.com/kendo-ui/framework/globalization/numberformatting
     */
    public format: any;
    /**
     * Der Name der CultureInfo
     */
    public culture: string;
    /**
     * Der minimale Wert
     */
    public min: KnockoutObservable<number>;
    /**
     * Der maximale Wert
     */
    public max: KnockoutObservable<number>;
    /**
     * Die Schrittgröße
     */
    public step: KnockoutObservable<number>;
    /**
     * Die maximale Länge des Eingabefeldes
     */
    public maxLength: number;
    /**
     * Gibt an, ob die Spin-Buttons angezeigt werden sollen
     */
    public showSpinners: KnockoutObservable<boolean>;
    /**
     * Das ViewModel für die Einheit des Wertes
     */
    public label: LabelViewModel;
    public isCreatedFromTextBox: KnockoutObservable<boolean>;
    public hasFocus: KnockoutObservable<boolean>;
    /**
     * .Net Typ von der Textbox
     */
    public dotNetTypeName: string;
    public _floatingPrecision: any;
    private language: any;
    constructor(params: any) {
        super(params);
        this.useUnlimitedDecimals = this.paramOrDefault(params.useUnlimitedDecimals, false);
        this.language = ResourceManager.getInstance().getLanguage();
        this.value = this.observableOrDefault(params.value, '');
        this.defineSubscription(this.value, (newValue) => {
            if (!this.hasFocus() && this.displayValue() !== newValue) {
                this.displayValue(newValue);
            }
        });
        this.displayValue = this.observableOrDefault(ko.unwrap(params.value), '');
        this.hasFocus = this.observableOrDefault(params.hasFocus, false);
        this.defineSubscription(this.hasFocus, (newValue) => {
            if (newValue === false) {
                // Das Timeout ist notwendig, weil der Wert im Widget zu diesem Zeitpunkt noch nicht
                // aktualisiert ist. Erst nach dem Event-Zyklus steht der finale Wert fest, mit dem
                // displayValue dann abgeglichen werden kann.
                setTimeout(() => {
                    if (!this.hasFocus() && this.displayValue() !== this.value()) {
                        this.displayValue(this.value());
                    }
                });
            }
        });
        this.dotNetTypeName = this.paramOrDefault(params.dotNetTypeName, '');
        this._floatingPrecision = this.observableOrDefault(params.floatingPrecision);
        this.floatingPrecision = this.defineComputed({
            read: () => {
                // Bei Ganzzahltypen muss immer 0 als FloatingPrecision zurückgegeben werden
                if (!Utilities.isNullOrUndefinedOrBlank(this.dotNetTypeName) &&
                    TypeConverter.typeIsNonFloatingNumber(this.dotNetTypeName)) {
                    return 0;
                } else {
                    if (!Utilities.isNullOrUndefinedOrBlank(this._floatingPrecision()))
                        return ko.unwrap(this._floatingPrecision);
                    else
                        return this.language.floatingPrecision;
                }
            },
            write: (value) => {
                this._floatingPrecision(value);
            }
        });
        this.format = this.paramOrDefault(params.format, null);
        this.culture = this.paramOrDefault(params.culture, this.language.cultureInfoName);
        this.min = this.observableOrDefault(params.min, Number.MIN_SAFE_INTEGER, this.GetSaveMinLimit);
        this.max = this.observableOrDefault(params.max, Number.MAX_SAFE_INTEGER, this.GetSaveMaxLimit);
        this.step = this.observableOrDefault(params.step, 1, parseFloat);
        this.maxLength = this.paramOrDefault(params.maxLength, -1);
        this.showSpinners = this.observableOrDefault(params.showSpinners, false);
        this.onChange = this.handlerOrDefault(params.onChange);
        this.onKeyDown = this.handlerOrDefault(params.onKeyDown);
        this.onKeyUp = this.handlerOrDefault(params.onKeyUp);
        this.label = this.paramOrDefault(params.label, null);
        this.isCreatedFromTextBox = this.observableOrDefault(params.isCreatedFromTextBox, false);
        this.onSubmit = this.handlerOrDefault(params.onSubmit);
        ControlViewModel.checkProperties(this, params);
    }
    public resetValue(): any {
        this.value(null);
        this.displayValue(null);
    }
    private GetSaveMaxLimit(value: any, limit: any): any {
        return (value == null || isNaN(value) || +value > limit) ? limit : +value;
    }
    private GetSaveMinLimit(value: any, limit: any): any {
        return (value == null || isNaN(value) || +value < limit) ? limit : +value;
    }
    /**
     * Ein Handler, der aufgerufen wird, wenn sich der Wert verändert
     */
    public onChange: Function;
    /**
     * Ein Handler, der aufgerufen wird, wenn der Benutzer eine Taste drückt
     */
    public onKeyDown: Function;
    /**
     * Ein Handler, der aufgerufen wird, wenn der Benutzer eine Taste loslässt
     */
    public onKeyUp: Function;
    /**
     * Ein Handler, der aufgerufen wird, wenn der Benutzer Enter drückt
     */
    public onSubmit: Function;
}

ComponentManager.setViewModel('NumericTextBoxViewModel', NumericTextBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');